import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { PasswordHintTooltip } from "@/components";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { useSuccessToast } from "@/hooks";
import { useResetPassword } from "@/hooks/mutations";

const resetPasswordSchema = z
  .object({
    oldPassword: z.string().min(1, "Required"),
    newPassword: z
      .string()
      .min(1, "Required")
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Za-z]/, "Password must contain at least one letter")
      .regex(/[0-9]/, "Password must contain at least one number")
      .regex(/[^A-Za-z0-9]/, "Password must contain at least one symbol"),
    confirmPassword: z.string().min(1, "Required"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords must match",
    path: ["confirmPassword"],
  });

export function ResetPasswordDialog() {
  const { mutate: resetPassword, status } = useResetPassword();
  const [open, setOpen] = useState(false);
  const successToast = useSuccessToast();

  const form = useForm<z.infer<typeof resetPasswordSchema>>({
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    resolver: zodResolver(resetPasswordSchema),
  });

  const handleSubmit = (data: z.infer<typeof resetPasswordSchema>) => {
    resetPassword(
      {
        resetPasswordRequest: {
          oldPassword: data.oldPassword,
          newPassword: data.newPassword,
          confirmPassword: data.confirmPassword,
        },
      },
      {
        onSuccess: () => {
          // TODO: Improve this...
          form.reset();
          setOpen(false);
          successToast({
            description: "Password reset successfully!",
          });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          form.reset();
        }
        setOpen(open);
      }}
    >
      <DialogTrigger asChild>
        <Button
          className="p-0 font-normal text-slate-700 underline"
          variant="link"
        >
          Need to reset your password?
        </Button>
      </DialogTrigger>
      <DialogContent>
        <p className="text-2xl font-semibold text-slate-700">Reset Password</p>
        <Form {...form}>
          <form className="flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="oldPassword"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Old Password</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <div className="flex items-center gap-2">
                      <FormLabel>New Password</FormLabel>
                      <PasswordHintTooltip />
                    </div>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="text-slate-700">
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                className="rounded-full"
                onClick={() => {
                  setOpen(false);
                }}
                type="button"
                variant="secondary"
              >
                Cancel
              </Button>
              <Button
                className="rounded-full"
                disabled={status === "pending"}
                onClick={(e) => {
                  // TODO: Improve this...
                  // Perform the submit here because this is in a wider form.
                  e.preventDefault();
                  void form.handleSubmit(handleSubmit)();
                }}
                type="submit"
              >
                Change password
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
